import * as React from 'react';
import type { DialogDescriptionProps, DialogOverlayProps, DialogTitleProps } from '@radix-ui/react-dialog';
import { Drawer as DrawerPrimitive } from 'vaul';

import { type ExtendedComponentProps } from '../types/common';
import { cn } from '../utils';

/* -------------------------------------------------------------------------------------------------
 * Drawer
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerPortal: typeof DrawerPrimitive.Portal = DrawerPrimitive.Portal;

const UiDrawer = ({ shouldScaleBackground = true, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
	<DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
UiDrawer.displayName = 'Drawer';

/* -------------------------------------------------------------------------------------------------
 * Drawer / Trigger
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerTrigger = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Trigger>,
	React.PropsWithChildren<ExtendedComponentProps<{ asChild: boolean }, 'button'>>
>((props, ref) => <DrawerPrimitive.Trigger ref={ref} {...props} />);
UiDrawerTrigger.displayName = DrawerPrimitive.Trigger.displayName;

/* -------------------------------------------------------------------------------------------------
 * Drawer / Close
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerClose = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Close>,
	React.PropsWithChildren<ExtendedComponentProps<{ asChild: boolean }, 'button'>>
>((props, ref) => <DrawerPrimitive.Close ref={ref} {...props} />);
UiDrawerClose.displayName = DrawerPrimitive.Close.displayName;

/* -------------------------------------------------------------------------------------------------
 * Drawer / Overlay
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerOverlay = React.forwardRef<React.ElementRef<typeof DrawerPrimitive.Overlay>, ExtendedComponentProps<DialogOverlayProps>>(
	({ className, ...props }, ref) => (
		<DrawerPrimitive.Overlay
			ref={ref}
			/** @ts-ignore - @radix-ui/react-dialog does accept className */
			className={cn('fixed inset-0 z-[210] bg-black/80', className)}
			{...props}
		/>
	),
);
UiDrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

/* -------------------------------------------------------------------------------------------------
 * Drawer / Content
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerContent: typeof DrawerPrimitive.Content = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Content>,
	React.PropsWithChildren<ExtendedComponentProps<{}, typeof DrawerPrimitive.Content>>
>(({ className, children, ...props }, ref) => (
	<UiDrawerPortal>
		<UiDrawerOverlay />
		<DrawerPrimitive.Content
			ref={ref}
			/** @ts-ignore - @radix-ui/react-dialog does accept className */
			className={cn(
				'fixed inset-x-0 bottom-0 z-[211] mt-24 max-h-[96%] flex h-auto flex-col rounded-t-[10px] border bg-white',
				'dark:bg-zinc-950 dark:border-zinc-800 border-zinc-200',
				className,
			)}
			{...props}
		>
			<div className={cn('mx-auto mt-4 h-1 w-[85px] rounded-full bg-zinc-100', 'dark:bg-zinc-800')} />
			{children}
		</DrawerPrimitive.Content>
	</UiDrawerPortal>
));
UiDrawerContent.displayName = 'UiDrawerContent';

/* -------------------------------------------------------------------------------------------------
 * Drawer / Header
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerHeader: React.FC<ExtendedComponentProps<{}>> = ({ className, ...props }) => (
	<div className={cn('grid gap-1.5 p-4 text-center sm:text-left overflow-auto', className)} {...props} />
);
UiDrawerHeader.displayName = 'UiDrawerHeader';

/* -------------------------------------------------------------------------------------------------
 * Drawer / Footer
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerFooter: React.FC<ExtendedComponentProps<{}>> = ({ className, ...props }) => (
	<div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
UiDrawerFooter.displayName = 'UiDrawerFooter';

/* -------------------------------------------------------------------------------------------------
 * Drawer / Title
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerTitle = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Title>,
	React.PropsWithChildren<ExtendedComponentProps<DialogTitleProps, 'h1'>>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Title ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />
));
UiDrawerTitle.displayName = DrawerPrimitive.Title.displayName;

/* -------------------------------------------------------------------------------------------------
 * Drawer / Description
 * -----------------------------------------------------------------------------------------------*/

const UiDrawerDescription = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Description>,
	React.PropsWithChildren<ExtendedComponentProps<DialogDescriptionProps, 'p'>>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Description ref={ref} className={cn('text-sm text-zinc-500', 'dark:text-zinc-400', className)} {...props} />
));
UiDrawerDescription.displayName = DrawerPrimitive.Description.displayName;

/* -----------------------------------------------------------------------------------------------*/

export {
	UiDrawer,
	UiDrawerPortal,
	UiDrawerOverlay,
	UiDrawerTrigger,
	UiDrawerClose,
	UiDrawerContent,
	UiDrawerHeader,
	UiDrawerFooter,
	UiDrawerTitle,
	UiDrawerDescription,
};
